/**
 * ProfessionalRoute - Entry point for /health-tracker/professional.
 *
 * Same UI as ProfessionalReportPage (month selector, PDF download, print,
 * nav bar, staff-only redirect), but reads the customerId from an encrypted
 * `customerId` query param using the AES scheme as
 * src/ts-lib/health-tracker/App.tsx (key: "CareConcierge").
 *
 * Example URL:
 *   /health-tracker/professional?customerId=<urlencoded-AES-ciphertext>
 */

import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
// @ts-ignore - no bundled types
import html2pdf from 'html2pdf.js';
// @ts-ignore - no bundled types
import CryptoJS from 'crypto-js';
import MonthlyReportPage from './components/MonthlyReportPage';

const ENC_KEY = 'CareConcierge';

const ProfessionalRoute: React.FC = () => {
  const navigate = useNavigate();

  // Decrypt the customerId from the query string.
  // Memoized so we keep the original encrypted value to forward on redirect.
  const { customerId, encryptedParam } = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    const encryptedParam = params.get('customerId') || '';
    let customerId = '0';
    if (encryptedParam) {
      try {
        const decoded = decodeURIComponent(encryptedParam);
        const bytes = CryptoJS.AES.decrypt(decoded, ENC_KEY);
        const decryptedId = bytes.toString(CryptoJS.enc.Utf8);
        if (decryptedId) customerId = decryptedId;
      } catch {
        // fall through with default '0'
      }
    }
    return { customerId, encryptedParam };
  }, []);

  // Staff-only access: redirect family users back to the patient dashboard if
  // they navigate here directly. The button that opens this page in
  // PatientInfoPanel.tsx is already gated, but the route can be hit via URL.
  useEffect(() => {
    const storedUserRole = localStorage.getItem('careGuardianUserRole');
    if (storedUserRole && storedUserRole !== 'staff') {
      const target = encryptedParam
        ? `/health_tracker?customerId=${encryptedParam}`
        : '/health_tracker';
      navigate(target, { replace: true });
    }
  }, [encryptedParam, navigate]);

  const [selectedMonth, setSelectedMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [isDownloading, setIsDownloading] = useState(false);

  const year = parseInt(selectedMonth.split('-')[0], 10);
  const month = parseInt(selectedMonth.split('-')[1], 10);

  const handlePrint = useCallback(() => {
    window.print();
  }, []);

  const handleDownloadPdf = useCallback(async () => {
    const element = document.getElementById('monthly-report-content');
    if (!element) return;

    setIsDownloading(true);

    // Remove the page-break mini headers from the DOM for PDF (we'll add headers via jsPDF)
    const miniHeaders = element.querySelectorAll('.page-mini-header');
    miniHeaders.forEach((el) => ((el as HTMLElement).style.display = 'none'));

    // Temporarily adjust styles for PDF rendering
    element.style.maxWidth = 'none';
    element.style.width = '1400px';
    element.style.padding = '20px';
    element.style.fontSize = '11px';

    const opt = {
      margin: [18, 8, 8, 8] as [number, number, number, number], // top margin 18mm for header space on pages 2+
      filename: `health_report_${customerId}_${selectedMonth}.pdf`,
      image: { type: 'jpeg' as const, quality: 0.95 },
      html2canvas: {
        scale: 2,
        useCORS: true,
        letterRendering: true,
        scrollY: 0,
        scrollX: 0,
        width: 1400,
      },
      jsPDF: {
        unit: 'mm' as const,
        format: 'a3' as const,
        orientation: 'portrait' as const,
      },
      pagebreak: { mode: ['css', 'legacy'] },
    };

    try {
      const worker = html2pdf().set(opt).from(element);
      const pdf = await worker.toPdf().get('pdf');

      // Get patient info from the rendered header
      const headerEl = element.querySelector('.report-header');
      const patientNameEl =
        headerEl?.querySelector('td:nth-child(2)')?.textContent?.replace(/^:\s*/, '') || '';
      const clientName = patientNameEl || customerId;

      const totalPages = pdf.internal.getNumberOfPages();
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const reportMonth = `${monthNames[month - 1]} ${year}`;

      const companyNameEl = element.querySelector('.report-header h2');
      const companyName = companyNameEl?.nextElementSibling?.textContent || '';

      // Add mini header to pages 2+
      for (let i = 2; i <= totalPages; i++) {
        pdf.setPage(i);
        pdf.setDrawColor(200, 200, 200);
        pdf.setLineWidth(0.3);
        pdf.line(8, 14, 289, 14); // A3 width = 297mm, margin 8mm each side

        pdf.setFontSize(8);
        pdf.setTextColor(44, 62, 80);
        pdf.setFont('helvetica', 'bold');
        pdf.text(companyName || 'Health Report', 8, 10);

        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(127, 140, 141);
        const rightText = `${clientName} | ${reportMonth} | Page ${i} of ${totalPages}`;
        const textWidth = (pdf.getStringUnitWidth(rightText) * 8) / pdf.internal.scaleFactor;
        pdf.text(rightText, 289 - textWidth, 10);
      }

      const safeClientName = clientName.replace(/[^a-zA-Z0-9\s]/g, '').replace(/\s+/g, '_');
      const monthStr = monthNames[month - 1];
      pdf.save(`${monthStr}_${year}_${safeClientName}_${customerId}.pdf`);
    } catch (err) {
      console.error('PDF generation failed:', err);
    } finally {
      element.style.maxWidth = '1400px';
      element.style.width = '';
      element.style.padding = '1.5rem';
      element.style.fontSize = '';
      miniHeaders.forEach((el) => ((el as HTMLElement).style.display = ''));
      setIsDownloading(false);
    }
  }, [customerId, selectedMonth, month, year]);

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f8f9fa' }}>
      {/* Print-specific styles */}
      <style>{`
        @media print {
          #report-nav-bar { display: none !important; }
          body, html { background: #fff !important; }
          #monthly-report-content {
            max-width: none !important;
            padding: 0 !important;
            margin: 0 !important;
            box-shadow: none !important;
          }
          @page { size: A3 portrait; margin: 15mm 10mm 10mm 10mm; }
          .report-header,
          .executive-summary,
          .vital-chart-container,
          .output-analytics-grid > div { page-break-inside: avoid; break-inside: avoid; }
          table tr { page-break-inside: avoid; break-inside: avoid; }
          [style*="border-radius"],
          [style*="borderRadius"] { page-break-inside: avoid; break-inside: avoid; }
          .output-analytics { page-break-before: always; break-before: always; }
          .care-notes-timeline { page-break-before: always; break-before: always; }
          .input-meal-matrix { page-break-before: always; break-before: always; }
          h3, h4 { page-break-after: avoid; break-after: avoid; }
          .vitals-grid { page-break-inside: avoid; break-inside: avoid; }
          .monthly-report-page > div:nth-child(3) { page-break-inside: avoid; break-inside: avoid; }
        }
      `}</style>

      {/* Top Navigation Bar */}
      <div
        id="report-nav-bar"
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 10,
          backgroundColor: '#fff',
          borderBottom: '1px solid #e0e0e0',
          padding: '0.75rem 1.5rem',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <button
            onClick={() =>
              navigate(
                encryptedParam ? `/health_tracker?customerId=${encryptedParam}` : '/health_tracker'
              )
            }
            style={{
              background: 'none',
              border: '1px solid #ccc',
              borderRadius: '6px',
              padding: '0.4rem 0.8rem',
              cursor: 'pointer',
              fontSize: '0.85rem',
              color: '#555',
            }}
          >
            ← Patient Dashboard
          </button>
          <h1 style={{ margin: 0, fontSize: '1.2rem', color: '#2c3e50', fontWeight: 700 }}>
            Professional Monthly Report (View only)
          </h1>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <label htmlFor="month-picker" style={{ fontSize: '0.85rem', color: '#666' }}>
            Month:
          </label>
          <input
            type="month"
            id="month-picker"
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
            style={{
              padding: '0.4rem 0.6rem',
              border: '1px solid #ccc',
              borderRadius: '6px',
              fontSize: '0.85rem',
            }}
          />
          <button
            onClick={handleDownloadPdf}
            disabled={isDownloading}
            style={{
              padding: '0.4rem 1rem',
              border: 'none',
              borderRadius: '6px',
              backgroundColor: isDownloading ? '#95a5a6' : '#2c3e50',
              color: '#fff',
              fontSize: '0.85rem',
              fontWeight: 600,
              cursor: isDownloading ? 'not-allowed' : 'pointer',
            }}
          >
            {isDownloading ? 'Generating PDF...' : '📄 Download PDF'}
          </button>
          <button
            onClick={handlePrint}
            style={{
              padding: '0.4rem 1rem',
              border: '1px solid #5A46AA',
              borderRadius: '6px',
              backgroundColor: '#fff',
              color: '#5A46AA',
              fontSize: '0.85rem',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            🖨️ Print
          </button>
        </div>
      </div>

      {/* Report Content */}
      <div
        id="monthly-report-content"
        style={{ padding: '1.5rem', maxWidth: '1400px', margin: '0 auto', backgroundColor: '#fff' }}
      >
        <MonthlyReportPage customerId={customerId} year={year} month={month} patientName="" />
      </div>
    </div>
  );
};

export default ProfessionalRoute;
